import { useEffect } from 'react';
import useWebSocket from 'react-use-websocket';

import { webSocketInstalledProducts } from '../solaris-lens-common-lib/src/constants/endpoints';
import { setSubscriptionMessage } from '../solaris-lens-common-lib/src/utils/webSocket';

export interface WebSocketProps {
  link?: string;
  options?: any;
  connectWs?: boolean;
  entityName?: string;
  entityId?: string;
  params?: string;
  onLastWsMessage?: (data: any) => void;
  binaryType?: string;
  args?: any[];
}

const useSocket = ({
  link = webSocketInstalledProducts,
  options,
  connectWs = false,
  entityName = '',
  entityId = '',
  params = '',
  onLastWsMessage = () => {},
  binaryType = 'blob',
  args = []
}: WebSocketProps) => {
  const useWebSocketInfo = useWebSocket(
    link,
    {
      share: false,
      shouldReconnect: () => false,
      reconnectAttempts: 3,
      reconnectInterval: 3000,
      ...options
    },
    connectWs
  );

  const { sendJsonMessage, lastMessage, readyState, getWebSocket } =
    useWebSocketInfo;

  useEffect(() => {
    if (entityName === '' || entityId === '') return;
    if (readyState !== WebSocket.OPEN || !connectWs) return;

    const ws = getWebSocket();
    if (ws && 'binaryType' in ws) {
      (ws as WebSocket).binaryType = binaryType as BinaryType;
    }

    sendJsonMessage(
      setSubscriptionMessage({
        entityName,
        entityId,
        params,
        args: args as never[]
      })
    );
  }, [readyState, connectWs, entityName, entityId]);

  useEffect(() => {
    const ws = getWebSocket();
    if (!ws) return;

    if (!connectWs && ws.readyState === WebSocket.OPEN) {
      ws.close();
    }
  }, [connectWs]);

  useEffect(() => {
    if (lastMessage) {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const json = JSON.parse(reader.result as string);

          if (json?._meta?.class === entityName) {
            onLastWsMessage(json);
          }
        } catch (e) {
          console.error('Bad WS message', e);
        }
      };
      reader.readAsText(lastMessage?.data);
      return () => reader.abort();
    }
  }, [lastMessage, entityId, entityName, connectWs]);

  return useWebSocketInfo;
};

export default useSocket;
